/**
 * Aspect — Revive Mode (engine side)
 * --------------------------------------------------------------
 * Seamless loop between the live 3D tour and an AI-generated, interactive 2D
 * render of the exact same viewpoint:
 *
 *   TOUR ──capture()──▶ RENDER_2D ──restore()──▶ TOUR
 *
 * This module owns ONLY the WebGL side of that loop:
 *   - captureReviveState(): freeze the camera transform (position, yaw/pitch,
 *     FOV) and project every furniture entity's world AABB into 2D screen
 *     space (normalized 0..1 rects) so a DOM layer can draw clickable
 *     hitboxes over the flat render.
 *   - restoreReviveState(): snap the camera back to the captured pose, resume
 *     rendering, and hand the cross-fade timing to the overlay.
 *
 * The DOM overlay (image, hitboxes, pricing cards, Enter Tour button) lives in
 * ui/revive_overlay.js and communicates only through constructor callbacks —
 * no DOM reads/writes happen here, no pc.* usage happens there.
 *
 * Strictly engine-API based (works in any PlayCanvas app, no Editor assets).
 */

import * as pc from "playcanvas";
import { aspectSalePrice } from "../currency.js";
import { captureIdMasks, MAX_ID_ITEMS } from "./revive_id_pass.js";
import { extractOutlines } from "../scene/mask_outline.js";
import { separatedByWall } from "../scene/wall_geometry.js";

/** Revive state machine states. */
export const REVIVE_STATE = Object.freeze({
  TOUR: "tour", // live 3D, overlay hidden
  RENDER_2D: "render2d", // frozen: 2D image + hitboxes on top, WebGL paused
  TRANSITION: "transition", // cross-fading 2D → 3D
});

/** Furniture smaller than this on screen isn't worth a hitbox (px²). */
const MIN_HITBOX_AREA_PX = 26 * 26;

/** Merge every render-component AABB under an entity (world space). */
function entityWorldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let ok = false;
  for (const render of entity.findComponents("render")) {
    for (const mi of render.meshInstances) {
      if (!ok) {
        aabb.copy(mi.aabb);
        ok = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return ok ? aabb : null;
}

export class ReviveMode {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {pc.Entity} camera   entity carrying the camera component
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {{
   *   fp?: import('./first_person_controls.js').FirstPersonControls,
   *   overlay?: {
   *     show(state: object, opts: { image: string|null }): void,
   *     fadeOutAndRemove(ms: number): Promise<void>,
   *   },
   * }} [deps]
   */
  constructor(app, camera, scene, deps = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.fp = deps.fp ?? null;
    this.overlay = deps.overlay ?? null;
    this.dayNight = deps.dayNight ?? null;
    this.sceneView = deps.sceneView ?? null;

    /** @type {typeof REVIVE_STATE[keyof typeof REVIVE_STATE]} */
    this.state = REVIVE_STATE.TOUR;
    /** Last captured snapshot (camera + hitboxes). */
    this._captured = null;
    /** Exact walk pose to resume after Photoreal / Enter Tour. */
    this._returnPose = null;

    this.app.on("ui:revive", () => void this.enter());
  }

  // ------------------------------------------------------------------ capture

  /**
   * STEP 1 — 3D → 2D data extraction.
   * Snapshot the camera transform and project every placed furniture entity's
   * bounding box to normalized screen rects, with live pricing attached.
   * Pure data out — safe to serialize (e.g. to send to an AI render API).
   */
  captureReviveState() {
    const cam = this.camera.camera;
    const canvas = this.app.graphicsDevice.canvas;
    const vw = canvas.clientWidth || canvas.width;
    const vh = canvas.clientHeight || canvas.height;

    const pos = this.camera.getPosition().clone();
    // Yaw/pitch from the camera's actual forward vector — valid no matter which
    // controller (walk / overhead / tour glide) last drove the camera.
    const fwd = this.camera.forward;
    const yaw = (Math.atan2(-fwd.x, -fwd.z) * 180) / Math.PI;
    const pitch = (Math.asin(pc.math.clamp(fwd.y, -1, 1)) * 180) / Math.PI;

    const state = {
      capturedAt: Date.now(),
      hour: this.dayNight?.hour ?? null,
      viewport: { w: vw, h: vh },
      camera: {
        position: [pos.x, pos.y, pos.z],
        yaw,
        pitch,
        fov: cam.fov,
      },
      hitboxes: [],
    };

    const camPos = this.camera.getPosition();
    const screenPt = new pc.Vec3();
    const corner = new pc.Vec3();

    for (const [id, entity] of this.scene.entities) {
      if (!entity?.enabled) continue; // distance-culled rooms stay untagged
      const rec = this.scene.placements.get(id);
      if (!rec) continue;
      const aabb = entityWorldAABB(entity);
      if (!aabb) continue;

      // Furniture in the next room is still in front of the lens. Skip it
      // when a solid wall (not an open door) sits between the camera and it.
      const c0 = aabb.center;
      if (separatedByWall(this.scene.room, camPos.x, camPos.z, c0.x, c0.z)) continue;

      // Project all 8 AABB corners; every corner must be in FRONT of the
      // camera (behind-camera projections mirror and produce garbage rects).
      const c = aabb.center;
      const e = aabb.halfExtents;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      let behind = false;
      for (let i = 0; i < 8; i++) {
        corner.set(
          c.x + (i & 1 ? e.x : -e.x),
          c.y + (i & 2 ? e.y : -e.y),
          c.z + (i & 4 ? e.z : -e.z),
        );
        const toC = corner.clone().sub(camPos);
        if (toC.dot(this.camera.forward) < 0.05) {
          behind = true;
          break;
        }
        cam.worldToScreen(corner, screenPt);
        minX = Math.min(minX, screenPt.x);
        minY = Math.min(minY, screenPt.y);
        maxX = Math.max(maxX, screenPt.x);
        maxY = Math.max(maxY, screenPt.y);
      }
      if (behind) continue;

      // Clamp to the viewport and drop rects that are off-screen or tiny.
      const x0 = Math.max(0, minX);
      const y0 = Math.max(0, minY);
      const x1 = Math.min(vw, maxX);
      const y1 = Math.min(vh, maxY);
      if (x1 - x0 <= 0 || y1 - y0 <= 0) continue;
      if ((x1 - x0) * (y1 - y0) < MIN_HITBOX_AREA_PX) continue;

      const meta = this.scene.catalog?.get?.(rec.asset_id);
      const sale = aspectSalePrice(rec.price ?? meta?.price ?? 0, id);
      const fp = rec.footprint ?? meta?.footprint;
      const scale = rec.scale ?? 1;
      const cm = (v) => Math.round(v * scale * 100);
      state.hitboxes.push({
        id,
        name: meta?.name ?? rec.asset_id,
        brand: meta?.brand ?? "",
        category: meta?.category ?? null,
        price: rec.price ?? meta?.price ?? null,
        dims: fp ? `${cm(fp.w)}cm x ${cm(fp.d)}cm` : null,
        // Normalized 0..1 — the DOM layer stays correct at any element size.
        rect: { x: x0 / vw, y: y0 / vh, w: (x1 - x0) / vw, h: (y1 - y0) / vh },
        // Depth orders overlapping hitboxes (nearer furniture wins the hover).
        depth: aabb.center.distance(camPos),
        pricing: sale
          ? { list: sale.list, sale: sale.sale, pct: sale.pct }
          : { list: rec.price ?? 0, sale: rec.price ?? 0, pct: 0 },
      });
    }

    // Nearest furniture last in DOM order = on top for pointer events.
    state.hitboxes.sort((a, b) => b.depth - a.depth);
    this._captured = state;
    this._returnPose = {
      position: [pos.x, pos.y, pos.z],
      yaw,
      pitch,
      fov: cam.fov,
    };
    return state;
  }

  /**
   * Run the object-ID mask pass for the biggest on-screen hitboxes and attach
   * `outline` polygons (normalized 0..1) that hug each item's visible pixels.
   * Any failure leaves the plain rects in place — never blocks Revive Mode.
   */
  async _attachOutlines(state) {
    try {
      // Largest screen coverage first — those are the items users will hover.
      const ranked = [...state.hitboxes].sort(
        (a, b) => b.rect.w * b.rect.h - a.rect.w * a.rect.h,
      );
      const targets = new Map();
      for (const hb of ranked.slice(0, MAX_ID_ITEMS)) {
        const entity = this.scene.entities.get(hb.id);
        if (entity?.enabled) targets.set(hb.id, entity);
      }
      if (!targets.size) return;

      const occluders = [this.scene.roomRoot].filter(Boolean);
      const masks = await captureIdMasks(this.app, this.camera, targets, occluders);
      if (!masks) return;

      const hidden = new Set();
      for (const hb of state.hitboxes) {
        if (!targets.has(hb.id)) continue;
        const mask = masks.maskOf(hb.id);
        // Fully covered by a wall (or the door slab). No outline, no hitbox.
        if (!mask) {
          hidden.add(hb.id);
          continue;
        }
        const polys = extractOutlines(mask, masks.width, masks.height, {
          epsilon: 1.35,
          maxPolys: 4,
          minArea: 24,
        });
        if (polys.length) hb.outline = polys;
      }
      if (hidden.size) state.hitboxes = state.hitboxes.filter((hb) => !hidden.has(hb.id));
    } catch (err) {
      console.warn("[revive] outline extraction failed — using rects", err);
    }
  }

  // ------------------------------------------------------------------- enter

  /**
   * STEP 2 — switch to the interactive 2D render.
   * Captures state, snapshots the live frame as the placeholder "AI render"
   * (callers can pass a real generated image URL instead), pauses WebGL, and
   * hands everything to the DOM overlay.
   * @param {{ image?: string }} [opts]
   */
  async enter(opts = {}) {
    if (this.state !== REVIVE_STATE.TOUR || !this.overlay) return null;

    const state = this.captureReviveState();

    // Grab the current frame as the stand-in for the AI-generated render.
    // toDataURL must run inside the same frame as a render (the drawing buffer
    // is cleared after present unless preserveDrawingBuffer is on), so force
    // one frame and read it back on 'frameend'.
    let image = opts.image ?? null;
    if (!image) {
      image = await new Promise((resolve) => {
        this.app.once("frameend", () => {
          try {
            resolve(this.app.graphicsDevice.canvas.toDataURL("image/jpeg", 0.92));
          } catch {
            resolve(null); // tainted/unreadable buffer — overlay shows a styled fallback
          }
        });
        this.app.renderNextFrame = true;
      });
    }

    // Exact silhouettes: one flat-color ID render, then trace each item's
    // visible pixels into vector outlines. Falls back to rects on failure.
    await this._attachOutlines(state);

    // Freeze the walk controller and stop presenting frames — the GPU goes
    // idle while the user browses the 2D render.
    this.fp?.releaseLock?.();
    if (this.fp) this.fp.setEnabled(false);
    this._prevAutoRender = this.app.autoRender;
    this.app.autoRender = false;

    this.state = REVIVE_STATE.RENDER_2D;
    this.overlay.show(state, { image });
    this.app.fire("revive:entered", { hitboxes: state.hitboxes.length });
    return state;
  }

  /** Deep copy of the last capture (camera + hitboxes + outlines). */
  capturedSnapshot() {
    return this._captured ? structuredClone(this._captured) : null;
  }

  /**
   * Re-open a gallery snapshot as the interactive 2D layer (same hovers).
   * Uses the stored camera + hitboxes — does not recapture the live view.
   */
  async openSaved({ state, image, photoreal = false } = {}) {
    if (!this.overlay || !state) return false;
    if (this.state === REVIVE_STATE.TRANSITION) return false;

    this._captured = structuredClone(state);
    if (state.camera) this._returnPose = structuredClone(state.camera);

    if (this.state === REVIVE_STATE.TOUR) {
      this.fp?.releaseLock?.();
      if (this.fp) this.fp.setEnabled(false);
      this._prevAutoRender = this.app.autoRender;
      this.app.autoRender = false;
    }

    this.state = REVIVE_STATE.RENDER_2D;
    this.overlay.show(this._captured, { image: image ?? null, photoreal });
    return true;
  }

  // ----------------------------------------------------------------- restore

  /**
   * STEP 3 — seamless 2D → 3D transition ("Enter Tour").
   * Snaps the camera to the captured pose, renders one frame UNDER the still-
   * opaque overlay, then lets the overlay cross-fade away so the photo
   * appears to come alive.
   */
  async restoreReviveState({ fadeMs = 500 } = {}) {
    // A stuck Photoreal restore used to sit in TRANSITION forever (no frameend
    // after a long GPU-idle Photo-to-Render). Second click force-exits.
    if (this.state === REVIVE_STATE.TRANSITION) {
      this._forceTourResume();
      this.overlay?.remove();
      this.state = REVIVE_STATE.TOUR;
      this.app.fire("revive:restored", {});
      return true;
    }
    if (this.state !== REVIVE_STATE.RENDER_2D) return false;
    this.state = REVIVE_STATE.TRANSITION;

    try {
      // Do not fire "walk" — overhead disable flies to a default landing and
      // wipes the capture pose. Snap out, then stand exactly where the still was taken.
      this.sceneView?.exitImmediate?.();
      this._forceTourResume();
      this._applyReturnPose();

      const photoreal = Boolean(this.overlay?.root?.classList.contains("revive-layer--photoreal"));
      if (photoreal) {
        const img = this.overlay.root?.querySelector(".revive-layer__img");
        if (img) {
          img.removeAttribute("src");
          img.remove();
        }
      }
      await this._waitOneFrame(700);
      this._applyReturnPose();
      await this.overlay?.fadeOutAndRemove(photoreal ? 180 : fadeMs);
    } catch (err) {
      console.warn("[revive] restore failed", err);
      this.overlay?.remove();
    } finally {
      this._forceTourResume();
      this._applyReturnPose();
      this.state = REVIVE_STATE.TOUR;
      this.app.fire("revive:restored", {});
    }
    return true;
  }

  /** Put the walk camera on the exact Revive / Photoreal capture. */
  _applyReturnPose() {
    const pose = this._returnPose ?? this._captured?.camera;
    if (!pose?.position) return;
    const [x, y, z] = pose.position;
    if (this.fp) {
      this.fp.setEnabled(true);
      this.fp.adoptPose(new pc.Vec3(x, y, z), pose.yaw, pose.pitch, { exact: true });
    } else {
      this.camera.setPosition(x, y, z);
      this.camera.setEulerAngles(pose.pitch, pose.yaw, 0);
    }
    if (Number.isFinite(pose.fov)) this.camera.camera.fov = pose.fov;
  }

  /** Photoreal can leave the engine paused; always bring the tour loop back. */
  _forceTourResume() {
    this.app.autoRender = true;
    this.app.timeScale = 1;
    this.app.renderNextFrame = true;
    this.fp?.setEnabled?.(true);
  }

  _waitOneFrame(ms) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      const t = window.setTimeout(finish, ms);
      this.app.once("frameend", () => {
        window.clearTimeout(t);
        finish();
      });
      this.app.renderNextFrame = true;
    });
  }
}

export default ReviveMode;
